import { z } from 'zod';
/**
 * Schema do diagnóstico produzido pela engine de análise.
 *
 * Versionado via schemaVersion para evolução sem quebrar consumidores.
 */
export const SeveritySchema = z.enum(['critical', 'suggestion', 'positive']);
export const SystemAffectedSchema = z.enum([
    'recruiter_search',
    'feed_algorithm',
    'ats',
    'first_impression',
]);
/**
 * Os 4 pilares de valor do SmartApply. Cada finding aponta quais pilares
 * o ajuste melhora. UI agrupa diagnóstico por pilar pra deixar claro qual
 * objetivo do user cada correção endereça.
 */
export const PillarSchema = z.enum(['views', 'interactions', 'visits', 'ats']);
export const PILLAR_LABELS = {
    views: {
        label: 'Visualizações',
        description: 'Aparecer mais em buscas e descoberta orgânica.',
    },
    interactions: {
        label: 'Interações',
        description: 'Engajamento nos posts (perfil dá credibilidade ao conteúdo).',
    },
    visits: {
        label: 'Visitas ao perfil',
        description: 'Perfil atrativo que retém quem chega.',
    },
    ats: {
        label: 'ATS / Recruiter Search',
        description: 'Passar em filtros automáticos e boolean search.',
    },
};
export const RewriteOptionSchema = z.object({
    label: z.string(),
    text: z.string(),
    rationale: z.string(),
});
export const FindingSchema = z.object({
    id: z.string().min(1),
    severity: SeveritySchema,
    category: z.string().min(1),
    title: z.string().min(8),
    citedExcerpt: z.string().nullable(),
    technicalReason: z.string().min(20),
    systemsAffected: z.array(SystemAffectedSchema).min(1),
    // Quais dos 4 pilares de valor esse finding melhora quando endereçado.
    // Pelo menos 1 obrigatório; LLM mapeia explicitamente.
    affectedPillars: z.array(PillarSchema).min(1).default(['views']),
    proposedFix: z.string().nullable(),
    proposedFixOptions: z.array(RewriteOptionSchema).default([]),
    estimatedTimeMinutes: z.number().int().nullable(),
});
export const StrengthSchema = z.object({
    id: z.string().min(1),
    title: z.string().min(8),
    evidence: z.string(),
    whyItMatters: z.string(),
    preserveDuringRewrite: z.boolean().default(true),
});
export const RewrittenSectionSchema = z.object({
    section: z.enum(['headline', 'summary', 'about_en', 'experience']),
    context: z.string().nullable(),
    text: z.string(),
    charCount: z.number().int(),
    rationale: z.string(),
});
export const QuickWinSchema = z.object({
    findingId: z.string(),
    estimatedTimeMinutes: z.number().int(),
});
export const AnalysisOutputSchema = z.object({
    schemaVersion: z.literal(1),
    generatedAt: z.string(),
    modelUsed: z.string(),
    positioningSentence: z.string().min(10),
    strengths: z.array(StrengthSchema).min(1).max(8),
    findings: z.array(FindingSchema).min(1),
    quickWins: z.array(QuickWinSchema).max(8),
    rewrittenSections: z.array(RewrittenSectionSchema).default([]),
    meta: z.object({
        sectionsAnalyzed: z.array(z.string()),
        durationMs: z.number().int(),
        promptVersion: z.string(),
    }),
});
/**
 * Opções de execução do /analyze.
 */
export const AnalyzeOptionsSchema = z.object({
    depth: z.enum(['standard', 'deep']).default('standard'),
    includeRewrites: z.boolean().default(true),
    rewriteSections: z
        .array(z.enum(['headline', 'summary', 'about_en', 'experience']))
        .default(['headline', 'summary']),
});
// Lazy import pra evitar ciclo: AnalyzeRequest aceita profile opcional como
// fallback quando o snapshotId expirou no server.
import { LinkedInProfileSchema } from './pdf-snapshot.js';
export const AnalyzeRequestSchema = z.object({
    snapshotId: z.string().uuid(),
    options: AnalyzeOptionsSchema.optional(),
    // Fallback: se o byokStore perdeu o snapshot (restart/TTL), client manda
    // o profile do sessionStorage aqui pra evitar reupload do PDF.
    profile: LinkedInProfileSchema.optional(),
});
//# sourceMappingURL=analysis-output.js.map